/**
 * <collection-carousel-nav>
 *
 * Header-mounted prev/next buttons for the Collection Carousel redesign
 * (`sections/collection-list.liquid` with `layout_type: carousel`).
 *
 * The theme's real `<slideshow-control>` buttons must stay inside
 * `<slideshow-component>` (component.js wires them by `ref` + `on:click`
 * delegation). Rather than replicate the navigation / disabled logic, this
 * element proxies: its two SSR'd buttons `.click()` the real (CSS-hidden)
 * controls, and it mirrors their `disabled` state via a MutationObserver.
 * The theme keeps `nextIndex >= slides.length` etc. correct for us.
 *
 * Bare HTMLElement (no refs/events of its own), matching ScrollHint /
 * CollectionCarouselProgress. Stays `hidden` — and the real overlay arrows
 * stay the only control — if no slideshow is found or the script never runs.
 */
class CollectionCarouselNav extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #realPrev = null;
  /** @type {HTMLButtonElement | null} */
  #realNext = null;
  /** @type {HTMLButtonElement | null} */
  #prevBtn = null;
  /** @type {HTMLButtonElement | null} */
  #nextBtn = null;

  #observer = new MutationObserver(() => this.#syncDisabled());

  connectedCallback() {
    const section = this.closest('.shopify-section') ?? this.closest('.section-resource-list');
    const slideshow = section?.querySelector('slideshow-component');

    this.#realPrev = slideshow?.querySelector('.slideshow-control--previous') ?? null;
    this.#realNext = slideshow?.querySelector('.slideshow-control--next') ?? null;
    this.#prevBtn = this.querySelector('[data-nav="prev"]');
    this.#nextBtn = this.querySelector('[data-nav="next"]');

    if (!this.#realPrev || !this.#realNext || !this.#prevBtn || !this.#nextBtn) {
      this.hidden = true;
      return;
    }

    this.#prevBtn.addEventListener('click', this.#onPrev);
    this.#nextBtn.addEventListener('click', this.#onNext);

    this.#observer.observe(this.#realPrev, { attributes: true, attributeFilter: ['disabled'] });
    this.#observer.observe(this.#realNext, { attributes: true, attributeFilter: ['disabled'] });

    this.#syncDisabled();
    this.hidden = false;
  }

  disconnectedCallback() {
    this.#prevBtn?.removeEventListener('click', this.#onPrev);
    this.#nextBtn?.removeEventListener('click', this.#onNext);
    this.#observer.disconnect();
  }

  #onPrev = () => this.#realPrev?.click();
  #onNext = () => this.#realNext?.click();

  #syncDisabled = () => {
    if (this.#prevBtn && this.#realPrev) this.#prevBtn.disabled = this.#realPrev.disabled;
    if (this.#nextBtn && this.#realNext) this.#nextBtn.disabled = this.#realNext.disabled;
  };
}

if (!customElements.get('collection-carousel-nav')) {
  customElements.define('collection-carousel-nav', CollectionCarouselNav);
}
